// src/pages/orders/orderDetailsScreen/OrderDetailsScreen.tsx
import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import {
  MdRepeat,
  MdErrorOutline,
  MdLocalShipping,
  MdPayment,
  MdPhone,
  MdMessage,
  MdAutorenew,
  MdCheckCircle,
  MdDoneAll,
  MdCancel,
  MdAttachMoney,
  MdPrint,
  MdContentCopy,
} from "react-icons/md";
import { useTheme } from "../../../common/hooks/useTheme";
import {
  DeliveryService,
  DeliveryType,
  OrderStatus,
} from "../../../common/types/enums";
import { OrderItem } from "../../../common/types/types";
import { useRepeatOrder } from "../useRepeatOrder";
import { useOrderDetails } from "./useOrderDetails";
import { MobileHeader } from "../../../common/components/mobileHeader/MobileHeader";
import { ProductHeader } from "../../../common/components/productHeader/ProductHeader";
import { EmptyState } from "../../../common/components/emptyState/EmptyState";
import BottomModal from "../../../common/components/bottomModal/BottomModal";
import { TransitionModalContent } from "../../../common/components/transitionModalContent/TransitionModalContent";
import { ConfirmModalContent } from "../../../common/components/confirmModalContent/ConfirmModalContent";
import { PrintableOrder } from "../../../common/components/printableOrder/PrintableOrder";
import {
  getDeliveryStatusText,
  getOrderStatusText,
  getStatusColor,
  getStatusTextColor,
} from "../../../common/utils/functions/enumToText";
import styles from "./OrderDetailsScreen.module.scss";
import { PageMeta } from "../../../common/components/pageMeta/PageMeta";
import { usePrint } from "../../../common/hooks/usePrint";
import { useSelector } from "react-redux";
import {
  selectIsExecutor,
  selectIsAdmin,
  selectClientUserId,
} from "../../../store/slices/executorSlice";
import { normalizePhone } from "../../../common/utils/functions/normalizePhone";

const STATUS_BUTTONS: Record<
  number,
  { icon: typeof MdCheckCircle; label: string }
> = {
  [OrderStatus.PREPARING]: { icon: MdAutorenew, label: "В работу" },
  [OrderStatus.READY]: { icon: MdCheckCircle, label: "Готов" },
  [OrderStatus.DELIVERING]: { icon: MdLocalShipping, label: "Доставляется" },
  [OrderStatus.DELIVERED]: { icon: MdDoneAll, label: "Доставлен" },
  [OrderStatus.PICKED_UP]: { icon: MdCheckCircle, label: "Выдан" },
  [OrderStatus.CANCELLED]: { icon: MdCancel, label: "Отменить" },
};

export default function OrderDetailsScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { repeatOrder, isRepeatDisabled } = useRepeatOrder();
  const isExecutor = useSelector(selectIsExecutor);
  const isAdmin = useSelector(selectIsAdmin);
  const clientUserId = useSelector(selectClientUserId);
  const {
    order,
    updating,
    getDeliveryTypeText,
    getPaymentMethodText,
    getDeliveryTypeIcon,
    getPaymentMethodIcon,
    hasActiveTransition,
    transitionEndTime,
    canStartTransition,
    canManualTransition,
    availableTransitions,
    handleStatusUpdate,
    handleMarkPaid,
    refreshOrder,
    canManage,
    fullAddress,
    addressDetails,
    handleCopyAddress,
    copied,
  } = useOrderDetails(Number(id));

  const { reactToPrintFn, contentRef } = usePrint();

  const [transitionModalVisible, setTransitionModalVisible] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    action: "status" | "paid";
    status?: OrderStatus;
    title: string;
  } | null>(null);

  const showRepeat = !!clientUserId || (!isExecutor && !isAdmin);
  const showPrint = isExecutor || isAdmin;

  const repeatDisabled = order ? isRepeatDisabled(order) : true;
  const handleRepeat = () => {
    if (order) repeatOrder(order);
  };

  const cdekDelivery =
    order?.deliveryService === DeliveryService.CDEK && order.delivery?.length
      ? order.delivery[0]
      : null;

  const openCdekTracking = () => {
    if (cdekDelivery?.deliveryOrderId) {
      window.open(
        `https://www.cdek.ru/ru/tracking?order_id=${cdekDelivery.deliveryOrderId}`,
        "_blank",
      );
    }
  };

  const DeliveryIcon = order
    ? getDeliveryTypeIcon(order.deliveryType)
    : MdLocalShipping;
  const PaymentIcon = order
    ? getPaymentMethodIcon(order.paymentMethod)
    : MdPayment;

  const visibleTransitions = availableTransitions.filter(
    (s) => isAdmin || s !== OrderStatus.CANCELLED,
  );

  if (!order) {
    return (
      <div
        className={styles.container}
        style={{ backgroundColor: theme.background }}
      >
        <PageMeta customTitle="Заказ не найден" />
        <div className={styles.mobileOnly}>
          <MobileHeader title="Детали заказа" />
        </div>
        <div className={styles.desktopOnly}>
          <ProductHeader
            title="Детали заказа"
            isFavorite={false}
            showFavorite={false}
            textColor={theme.text}
            borderColor={theme.border}
          />
        </div>
        <EmptyState
          icon={MdErrorOutline}
          title="Заказ не найден"
          description={`Заказ с номером #${id} не найден в истории`}
        />
      </div>
    );
  }

  const subtitle = order.createdAt
    ? `Заказ #${order.id} • ${format(new Date(order.createdAt), "dd.MM.yyyy HH:mm", { locale: ru })}`
    : undefined;

  return (
    <div
      className={styles.container}
      style={{ backgroundColor: theme.background }}
    >
      <PageMeta />
      <div className={styles.mobileOnly}>
        <MobileHeader title="Детали заказа" subtitle={subtitle} />
      </div>
      <div className={styles.desktopOnly}>
        <ProductHeader
          title={`Детали заказа #${order.id}`}
          isFavorite={false}
          showFavorite={false}
          textColor={theme.text}
          borderColor={theme.border}
        />
      </div>

      <div className={styles.content}>
        {/* Контактные данные */}
        <div
          className={styles.section}
          style={{ backgroundColor: theme.card, borderColor: theme.border }}
        >
          <h2 className={styles.sectionTitle} style={{ color: theme.text }}>
            Контактные данные
          </h2>
          <div className={styles.infoRow}>
            <span
              className={styles.infoLabel}
              style={{ color: theme.textSecondary }}
            >
              Телефон:
            </span>
            <div className={styles.infoValueWithIcon}>
              <MdPhone size={16} color={theme.primary} />
              {order.phone_number ? (
                <a
                  href={`tel:${normalizePhone(order.phone_number)}`}
                  className={styles.infoValue}
                  style={{ color: theme.primary, textDecoration: "none" }}
                >
                  {order.phone_number}
                </a>
              ) : (
                <span
                  className={styles.infoValue}
                  style={{ color: theme.text }}
                >
                  Не указан
                </span>
              )}
            </div>
          </div>
          {order.description && (
            <div className={styles.commentBlock}>
              <div className={styles.infoValueWithIcon}>
                <MdMessage size={16} color={theme.primary} />
                <span
                  className={styles.commentText}
                  style={{ color: theme.text }}
                >
                  {order.description}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Статус + кнопки */}
        <div
          className={styles.section}
          style={{ backgroundColor: theme.card, borderColor: theme.border }}
        >
          <div className={styles.status}>
            <h2 className={styles.sectionTitle} style={{ color: theme.text }}>
              Статус
            </h2>
            <div
              className={styles.statusBadge}
              style={{ backgroundColor: getStatusColor(order.status) }}
            >
              <span
                className={styles.statusText}
                style={{ color: getStatusTextColor(order.status) }}
              >
                {getOrderStatusText(order.status)}
              </span>
            </div>
          </div>

          {hasActiveTransition && transitionEndTime && (
            <div className={styles.transitionInfo}>
              <MdAutorenew size={16} color={theme.primary} />
              <span className={styles.transitionText}>
                Автосмена активна. Завершится в{" "}
                {format(new Date(transitionEndTime), "HH:mm")}
              </span>
            </div>
          )}

          {canManage && canManualTransition && (
            <div className={styles.executorActions}>
              {canStartTransition && (
                <button
                  className={styles.actionButton}
                  onClick={() => setTransitionModalVisible(true)}
                  disabled={updating}
                >
                  <MdAutorenew size={20} color={theme.onPrimary} />
                  <span>Запустить цепочку</span>
                </button>
              )}

              {visibleTransitions.map((status) => {
                const btn = STATUS_BUTTONS[status];
                if (!btn) return null;
                return (
                  <button
                    key={status}
                    className={styles.actionButton}
                    onClick={() =>
                      setConfirmModal({
                        action: "status",
                        status,
                        title: `Перевести заказ в статус "${btn.label}"?`,
                      })
                    }
                    disabled={updating}
                  >
                    <btn.icon size={20} color={theme.onPrimary} />
                    <span>{btn.label}</span>
                  </button>
                );
              })}

              {canManage && !order.isPaid && (
                <button
                  className={styles.actionButton}
                  onClick={() =>
                    setConfirmModal({
                      action: "paid",
                      title: "Отметить заказ как оплаченный?",
                    })
                  }
                  disabled={updating}
                  style={{ backgroundColor: theme.success }}
                >
                  <MdAttachMoney size={20} color="#fff" />
                  <span>Оплачено</span>
                </button>
              )}
            </div>
          )}

          {order.createdAt && (
            <div className={styles.infoRow}>
              <span
                className={styles.infoLabel}
                style={{ color: theme.textSecondary }}
              >
                Дата создания:
              </span>
              <span className={styles.infoValue} style={{ color: theme.text }}>
                {format(new Date(order.createdAt), "dd.MM.yyyy HH:mm", {
                  locale: ru,
                })}
              </span>
            </div>
          )}
          {order.updatedAt && (
            <div className={styles.infoRow}>
              <span
                className={styles.infoLabel}
                style={{ color: theme.textSecondary }}
              >
                Последнее обновление:
              </span>
              <span className={styles.infoValue} style={{ color: theme.text }}>
                {format(new Date(order.updatedAt), "dd.MM.yyyy HH:mm", {
                  locale: ru,
                })}
              </span>
            </div>
          )}
        </div>

        {/* Доставка */}
        <div
          className={styles.section}
          style={{ backgroundColor: theme.card, borderColor: theme.border }}
        >
          <h2 className={styles.sectionTitle} style={{ color: theme.text }}>
            Доставка
          </h2>
          <div className={styles.infoRow}>
            <span
              className={styles.infoLabel}
              style={{ color: theme.textSecondary }}
            >
              Тип доставки:
            </span>
            <div className={styles.infoValueWithIcon}>
              <DeliveryIcon size={16} color={theme.primary} />
              <span className={styles.infoValue} style={{ color: theme.text }}>
                {getDeliveryTypeText(order.deliveryType)}
              </span>
            </div>
          </div>
          {Number(order.deliveryType) === DeliveryType.DELIVERY &&
            !cdekDelivery &&
            fullAddress && (
              <div
                className={`${styles.addressCard} ${canManage ? styles.clickable : ""}`}
                style={{ backgroundColor: theme.background }}
                onClick={canManage ? handleCopyAddress : undefined}
              >
                {canManage && (
                  <div className={styles.copyIconWrapper}>
                    {copied && (
                      <span className={styles.copyTooltip}>Скопировано!</span>
                    )}
                    <MdContentCopy size={18} color={theme.primary} />
                  </div>
                )}
                <p className={styles.addressText} style={{ color: theme.text }}>
                  {fullAddress.address?.value}
                </p>
                <p className={styles.addressText} style={{ color: theme.text }}>
                  {fullAddress.city?.value}
                </p>
                {addressDetails.length > 0 && (
                  <div className={styles.addressDetailsContainer}>
                    {addressDetails.map((detail, index) => {
                      const IconComponent = detail.icon;
                      return (
                        <div key={index} className={styles.addressDetailItem}>
                          <IconComponent size={12} color={theme.primary} />
                          <span
                            className={styles.addressDetailText}
                            style={{ color: theme.text }}
                          >
                            {detail.text}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {fullAddress.description && (
                  <p
                    className={styles.addressComment}
                    style={{ color: theme.textSecondary }}
                  >
                    Комментарий: {fullAddress.description}
                  </p>
                )}
              </div>
            )}
          {Number(order.deliveryType) === DeliveryType.PICKUP &&
            order.store && (
              <div
                className={`${styles.addressCard} ${canManage ? styles.clickable : ""}`}
                style={{ backgroundColor: theme.background }}
                onClick={canManage ? handleCopyAddress : undefined}
              >
                {canManage && (
                  <div className={styles.copyIconWrapper}>
                    <MdContentCopy size={18} color={theme.primary} />
                    {copied && (
                      <span className={styles.copyTooltip}>Скопировано!</span>
                    )}
                  </div>
                )}
                <p className={styles.addressText} style={{ color: theme.text }}>
                  {order.store.name}
                </p>
                <p className={styles.addressText} style={{ color: theme.text }}>
                  {order.store.address?.value}
                </p>
                <p className={styles.addressText} style={{ color: theme.text }}>
                  {order.store.city?.value}
                </p>
              </div>
            )}
        </div>

        {/* Доставка СДЭК */}
        {cdekDelivery && (
          <div
            className={styles.section}
            style={{ backgroundColor: theme.card, borderColor: theme.border }}
          >
            <h2 className={styles.sectionTitle} style={{ color: theme.text }}>
              Доставка СДЭК
            </h2>
            <div className={styles.infoRow}>
              <span
                className={styles.infoLabel}
                style={{ color: theme.textSecondary }}
              >
                Статус:
              </span>
              <span className={styles.infoValue} style={{ color: theme.text }}>
                {getDeliveryStatusText(cdekDelivery.status)}
              </span>
            </div>
            {cdekDelivery.tariffName && (
              <div className={styles.infoRow}>
                <span
                  className={styles.infoLabel}
                  style={{ color: theme.textSecondary }}
                >
                  Тариф:
                </span>
                <span
                  className={styles.infoValue}
                  style={{ color: theme.text }}
                >
                  {cdekDelivery.tariffName}
                </span>
              </div>
            )}
            {cdekDelivery.pickupPointName && (
              <div className={styles.infoRow}>
                <span
                  className={styles.infoLabel}
                  style={{ color: theme.textSecondary }}
                >
                  Пункт выдачи:
                </span>
                <span
                  className={styles.infoValue}
                  style={{ color: theme.text }}
                >
                  {cdekDelivery.pickupPointName}
                </span>
              </div>
            )}
            {cdekDelivery.pickupPointAddress && (
              <div className={styles.infoRow}>
                <span
                  className={styles.infoLabel}
                  style={{ color: theme.textSecondary }}
                >
                  Адрес ПВЗ:
                </span>
                <span
                  className={styles.infoValue}
                  style={{ color: theme.text }}
                >
                  {cdekDelivery.pickupPointAddress}
                </span>
              </div>
            )}
            {cdekDelivery.deliveryOrderId && (
              <button
                className={styles.cdekLink}
                onClick={openCdekTracking}
                style={{ color: theme.primary }}
              >
                Отследить на сайте СДЭК
              </button>
            )}
          </div>
        )}

        {/* Оплата */}
        <div
          className={styles.section}
          style={{ backgroundColor: theme.card, borderColor: theme.border }}
        >
          <h2 className={styles.sectionTitle} style={{ color: theme.text }}>
            Оплата
          </h2>
          <div className={styles.infoRow}>
            <span
              className={styles.infoLabel}
              style={{ color: theme.textSecondary }}
            >
              Способ оплаты:
            </span>
            <div className={styles.infoValueWithIcon}>
              <PaymentIcon size={16} color={theme.primary} />
              <span className={styles.infoValue} style={{ color: theme.text }}>
                {getPaymentMethodText(order.paymentMethod)}
              </span>
            </div>
          </div>
          <div className={styles.infoRow}>
            <span
              className={styles.infoLabel}
              style={{ color: theme.textSecondary }}
            >
              Оплачен:
            </span>
            <span
              className={styles.infoValue}
              style={{ color: order.isPaid ? theme.success : theme.danger }}
            >
              {order.isPaid ? "Да" : "Нет"}
            </span>
          </div>
        </div>

        {/* Товары */}
        <div
          className={styles.section}
          style={{ backgroundColor: theme.card, borderColor: theme.border }}
        >
          <h2 className={styles.sectionTitle} style={{ color: theme.text }}>
            Товары
          </h2>
          <div className={styles.itemsTable}>
            <div
              className={styles.tableHeader}
              style={{ borderBottomColor: theme.border }}
            >
              <span className={styles.tableHeaderText} style={{ flex: 2 }}>
                Название
              </span>
              <span
                className={styles.tableHeaderText}
                style={{ flex: 1, textAlign: "center" }}
              >
                Кол-во
              </span>
              <span
                className={styles.tableHeaderText}
                style={{ flex: 1, textAlign: "right" }}
              >
                Цена
              </span>
              <span
                className={styles.tableHeaderText}
                style={{ flex: 1, textAlign: "right" }}
              >
                Сумма
              </span>
            </div>
            {order.items?.map((item: OrderItem, index: number) => (
              <div
                key={index}
                className={styles.tableRow}
                style={{ borderBottomColor: `${theme.border}20` }}
              >
                <span
                  className={styles.itemName}
                  style={{ color: theme.text, flex: 2 }}
                >
                  {item.name}
                </span>
                <span
                  className={styles.itemQuantity}
                  style={{ color: theme.text, flex: 1, textAlign: "center" }}
                >
                  {item.quantity}
                </span>
                <span
                  className={styles.itemPrice}
                  style={{ color: theme.text, flex: 1, textAlign: "right" }}
                >
                  {Number(item.price).toLocaleString("ru-RU")} ₽
                </span>
                <span
                  className={styles.itemTotal}
                  style={{ color: theme.text, flex: 1, textAlign: "right" }}
                >
                  {Number(item.total).toLocaleString("ru-RU")} ₽
                </span>
              </div>
            ))}
          </div>
          <div
            className={styles.summaryRow}
            style={{ borderTopColor: theme.border }}
          >
            <span
              className={styles.summaryLabel}
              style={{ color: theme.textSecondary }}
            >
              Подытог
            </span>
            <span className={styles.summaryValue} style={{ color: theme.text }}>
              {Number(order.subtotal).toLocaleString("ru-RU")} ₽
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span
              className={styles.summaryLabel}
              style={{ color: theme.textSecondary }}
            >
              Доставка
            </span>
            <span className={styles.summaryValue} style={{ color: theme.text }}>
              {Number(order.deliveryFee).toLocaleString("ru-RU")} ₽
            </span>
          </div>
          <div
            className={styles.totalRow}
            style={{ borderTopColor: theme.primary }}
          >
            <span className={styles.totalLabel} style={{ color: theme.text }}>
              Итого
            </span>
            <span
              className={styles.totalValue}
              style={{ color: theme.primary }}
            >
              {Number(order.total).toLocaleString("ru-RU")} ₽
            </span>
          </div>
        </div>

        {order.notes && (
          <div
            className={styles.section}
            style={{ backgroundColor: theme.card, borderColor: theme.border }}
          >
            <h2 className={styles.sectionTitle} style={{ color: theme.text }}>
              Примечания
            </h2>
            <div
              className={styles.notesCard}
              style={{ backgroundColor: theme.background }}
            >
              <p className={styles.notesText} style={{ color: theme.text }}>
                {order.notes}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className={styles.mobileActionsContainer}>
        {showPrint && (
          <button className={styles.repeatButton} onClick={reactToPrintFn}>
            <MdPrint size={24} color={theme.onPrimary} />
            <span className={styles.repeatButtonText}>Печать</span>
          </button>
        )}
        {showRepeat && !repeatDisabled && (
          <button className={styles.repeatButton} onClick={handleRepeat}>
            <MdRepeat size={24} color={theme.onPrimary} />
            <span className={styles.repeatButtonText}>Повторить заказ</span>
          </button>
        )}
      </div>

      <div className={styles.desktopActionsContainer}>
        {showPrint && (
          <button className={styles.repeatButton} onClick={reactToPrintFn}>
            <MdPrint size={24} color={theme.onPrimary} />
            <span className={styles.repeatButtonText}>Печать</span>
          </button>
        )}
        {showRepeat && !repeatDisabled && (
          <button className={styles.repeatButton} onClick={handleRepeat}>
            <MdRepeat size={24} color={theme.onPrimary} />
            <span className={styles.repeatButtonText}>Повторить заказ</span>
          </button>
        )}
      </div>
      <div style={{ display: "none" }}>
        <div ref={contentRef}>{order && <PrintableOrder order={order} />}</div>
      </div>

      <BottomModal
        isVisible={transitionModalVisible}
        onClose={() => setTransitionModalVisible(false)}
      >
        <TransitionModalContent
          orderId={order.id!}
          onClose={() => {
            refreshOrder();
            setTransitionModalVisible(false);
          }}
        />
      </BottomModal>

      <BottomModal
        isVisible={!!confirmModal}
        onClose={() => setConfirmModal(null)}
      >
        <ConfirmModalContent
          title={confirmModal?.title || ""}
          confirmText="Да"
          cancelText="Нет"
          onConfirm={() => {
            if (confirmModal) {
              if (confirmModal.action === "paid") handleMarkPaid();
              else if (confirmModal.status !== undefined)
                handleStatusUpdate(confirmModal.status);
            }
            setConfirmModal(null);
          }}
          onCancel={() => setConfirmModal(null)}
          loading={updating}
        />
      </BottomModal>
    </div>
  );
}
